// Who is reading an invitation, relative to who it was written for.
//
// The invitation screen used to decide everything from the invitation record
// alone, so `account_exists` — a fact about the *invited address* — read as a
// fact about the *viewer* (#105). Signed in as somebody else you got the same
// card and the same button, and the button walked you into the inviting app
// under the wrong identity.
//
// Kept here rather than inline in the view because the view has no test harness:
// everything under web/lib is a pure function bun can run, and this comparison
// is the part worth covering.

/** The viewer is signed out, or signed in as nobody we can name. */
export const IDENTITY_ANONYMOUS = 'anonymous';

/** The viewer is signed in as the person this invitation is for. */
export const IDENTITY_MATCH = 'match';

/** The viewer is signed in as somebody else. */
export const IDENTITY_MISMATCH = 'mismatch';

/**
 * Compare the signed-in account against the invited address.
 *
 * Addresses are trimmed and lower-cased before comparing. The API already
 * lower-cases both when it stores them, so today they arrive equal — but this is
 * the comparison that decides whether someone is warned, and it should not
 * depend on a normalization performed somewhere else.
 *
 * A signed-in viewer whose email we cannot read is `anonymous`, not `mismatch`.
 * There is nothing to compare, and telling someone they are the wrong person on
 * the strength of missing data is worse than saying nothing: it is the same
 * false alarm every time, and it teaches them to click past the warning that
 * matters.
 * @param {{isAuthenticated?: boolean, currentEmail?: string, invitedEmail?: string}} params
 * @returns {typeof IDENTITY_ANONYMOUS | typeof IDENTITY_MATCH | typeof IDENTITY_MISMATCH}
 */
export function invitationIdentity({ isAuthenticated, currentEmail, invitedEmail }) {
    if (!isAuthenticated) return IDENTITY_ANONYMOUS;

    const current = normalize(currentEmail);
    const invited = normalize(invitedEmail);
    if (!current || !invited) return IDENTITY_ANONYMOUS;

    return current === invited ? IDENTITY_MATCH : IDENTITY_MISMATCH;
}

function normalize(email) {
    return typeof email === 'string' ? email.trim().toLowerCase() : '';
}
